import {useCallback, useEffect, useRef, useState} from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {ProcessesPluginBase} from 'processes/graph-builder-processes/platform/ProcessesPluginBase';
import {
  ProcessFlowchartValidationError,
  ValidationContext,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {
  findNewProcessFlowchartValidationErrors,
  trackTriggeredFlowchartValidationError,
} from 'processes/graph-builder-processes/validation/processFlowchartValidationAnalytics';
import validateProcessFlowchart from 'processes/graph-builder-processes/validation/validateProcessFlowchart';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import buildValidationFlowchartFromStaticData from './buildValidationFlowchartFromStaticData';
import {StaticTemplateFlowchart} from './processFlowchartConfigEditorConstants';
import {FlowchartConfigWithMetadata} from './redux/flowchartConfigAutosaveManagerCallbacks';
import {
  FlowchartConfigAutosaverPausedCondition,
  FlowchartConfigAutosaverPausedConditionType,
} from './redux/reduceFlowchartConfig';
import {useApplicationFlowchartValidationContext} from './useFlowchartValidationContext';
// Exporting for testing
export interface UseFlowchartValidationProps {
  draftFlowchartConfig: StaticTemplateFlowchart;
  flowchartConfigId: string;
  flowchartConfigMetadata: FlowchartConfigWithMetadata['metadata'];
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  // Note, this must be a memoized callback/plain function to avoid unnecessary re-renders
  setIsFlowchartConfigAutosaverPaused: (
    condition: FlowchartConfigAutosaverPausedConditionType,
    conditionValue: boolean
  ) => void;
  didInitialFlowchartHaveChainedRouters: boolean;
  plugin: ProcessesPluginBase;
  applicationValidationContext?: Record<string, unknown>;
  actionOrigin?: ActionOrigin;
}

// Exporting for testing
export interface UseFlowchartValidationReturn {
  validationErrors: Array<ProcessFlowchartValidationError>;
  validationErrorsRef: React.MutableRefObject<Array<ProcessFlowchartValidationError>>;
  setValidationErrors: (errors: Array<ProcessFlowchartValidationError>) => void;
  validateFlowchart: (draftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata) => void;
}

export function validateFlowchartData(
  staticFlowchartData: StaticTemplateFlowchart,
  flowchartConfigId: string,
  metadata: FlowchartConfigWithMetadata['metadata'],
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL,
  didInitialFlowchartHaveChainedRouters: boolean,
  plugin: ProcessesPluginBase,
  applicationValidationContext?: Record<string, unknown>,
  editorMode: FlowchartEditorMode = FlowchartEditorMode.MAIN_CANVAS
): Array<ProcessFlowchartValidationError> {
  const {flowchart, designSpace} = buildValidationFlowchartFromStaticData({
    id: flowchartConfigId,
    staticNodesData: staticFlowchartData.nodesStaticData,
    staticEdgesData: staticFlowchartData.edgesStaticData,
    options: {
      transitions: metadata.transitions,
      rootTaskSchema,
      currentVersionId: metadata.currentVersionId,
      didInitialFlowchartHaveChainedRouters: metadata.didInitialFlowchartHaveChainedRouters,
      flowchartTypeName: metadata.flowchartTypeName,
      options: metadata.options,
    },
  });

  const validationContext: ValidationContext = {
    editorMode,
    designSpace,
    didInitialFlowchartHaveChainedRouters,
    transitions: metadata.transitions,
    disconnectedNodeIds: [],
    applicationValidationContext,
  };

  const validationResult = validateProcessFlowchart({
    flowchart,
    validationContext,
    usingFlowchartFirstConfig: true,
    plugin,
  });
  return validationResult.errors;
}

// Module exporting to support test stubbing
export const ValidationModule = {
  validateFlowchartData,
};

export function useFlowchartValidationAndAutosaverControl({
  draftFlowchartConfig,
  flowchartConfigId,
  flowchartConfigMetadata,
  rootTaskSchema,
  setIsFlowchartConfigAutosaverPaused,
  didInitialFlowchartHaveChainedRouters,
  plugin,
  actionOrigin = ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
}: UseFlowchartValidationProps): UseFlowchartValidationReturn {
  const {
    context: applicationValidationContext,
    isLoaded: isApplicationValidationContextLoaded,
    contextKey: applicationValidationContextKey,
  } = useApplicationFlowchartValidationContext(rootTaskSchema.executionType, draftFlowchartConfig);
  const isApplicationValidationContextLoadedRef = useRef(isApplicationValidationContextLoaded);
  // Track the validation context key to detect when context data has changed
  const applicationValidationContextKeyRef = useRef<string | null>(null);

  const [validationErrors, _setValidationErrors] = useState<Array<ProcessFlowchartValidationError>>(() =>
    ValidationModule.validateFlowchartData(
      draftFlowchartConfig,
      flowchartConfigId,
      flowchartConfigMetadata,
      rootTaskSchema,
      didInitialFlowchartHaveChainedRouters,
      plugin,
      applicationValidationContext
    )
  );

  const validationErrorsRef = useRef(validationErrors);

  const setValidationErrors = useCallback((newValidationErrors) => {
    _setValidationErrors(newValidationErrors);
    validationErrorsRef.current = newValidationErrors;
  }, []);

  const validateFlowchart = useCallback(
    (draftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata) => {
      const previousValidationErrors = validationErrorsRef.current;
      const currentValidationErrors = ValidationModule.validateFlowchartData(
        draftFlowchartConfigAndMetadata.staticFlowchart,
        flowchartConfigId,
        draftFlowchartConfigAndMetadata.metadata,
        rootTaskSchema,
        didInitialFlowchartHaveChainedRouters,
        plugin,
        // Use the validation context computed at the top level. The hook values will update automatically
        // when the draft flowchart changes. Application teams can provide a custom validation context if
        // needed.
        applicationValidationContext
      );

      const newValidationErrors = findNewProcessFlowchartValidationErrors(
        previousValidationErrors,
        currentValidationErrors
      );
      if (newValidationErrors.length > 0) {
        trackTriggeredFlowchartValidationError(newValidationErrors, {
          flowchartConfigId,
          actionOrigin,
        });
      }

      setValidationErrors(currentValidationErrors);
    },
    [
      flowchartConfigId,
      rootTaskSchema,
      didInitialFlowchartHaveChainedRouters,
      plugin,
      setValidationErrors,
      applicationValidationContext,
      actionOrigin,
    ]
  );

  useEffect(() => {
    const contextHasChanged = applicationValidationContextKey !== applicationValidationContextKeyRef.current;

    if (isApplicationValidationContextLoaded && !isApplicationValidationContextLoadedRef.current) {
      isApplicationValidationContextLoadedRef.current = true;
      applicationValidationContextKeyRef.current = applicationValidationContextKey;
      validateFlowchart({
        staticFlowchart: draftFlowchartConfig,
        metadata: flowchartConfigMetadata,
      });
    } else if (isApplicationValidationContextLoaded && contextHasChanged) {
      applicationValidationContextKeyRef.current = applicationValidationContextKey;
      validateFlowchart({
        staticFlowchart: draftFlowchartConfig,
        metadata: flowchartConfigMetadata,
      });
    } else if (!isApplicationValidationContextLoaded) {
      isApplicationValidationContextLoadedRef.current = false;
    }
  }, [
    applicationValidationContextKey,
    isApplicationValidationContextLoaded,
    validateFlowchart,
    draftFlowchartConfig,
    flowchartConfigMetadata,
  ]);

  const setPausedForValidationErrors = useCallback(
    (isPaused: boolean) => {
      setIsFlowchartConfigAutosaverPaused(
        FlowchartConfigAutosaverPausedCondition.HAS_VALIDATION_ERRORS,
        isPaused
      );
    },
    [setIsFlowchartConfigAutosaverPaused]
  );
  useAutosaverPausingForValidationErrors(validationErrors, setPausedForValidationErrors);

  return {
    validationErrors,
    validationErrorsRef,
    setValidationErrors,
    validateFlowchart,
  };
}

const useAutosaverPausingForValidationErrors = (
  validationErrors: Array<ProcessFlowchartValidationError>,
  setAutosaverPaused: (isPaused: boolean) => void
): void => {
  useEffect(() => {
    const blockingErrors = validationErrors.filter((error) => error.blocksPersistence);
    setAutosaverPaused(blockingErrors.length > 0);
  }, [validationErrors, setAutosaverPaused]);
};
